import React from 'react';
import { Link } from 'react-router-dom';
import {
  CyclingEvent, CATEGORY_COLORS, CATEGORY_LABELS, EVENT_TYPE_LABELS, activePromotion,
  displayBundesland, eventCategories, eventCountdown, haversineDistance, promotionCta,
  registrationBadge, permanentBadge } from '@radevents/shared';
import { cardDistanceChips } from '../utils/eventHero';
import BookmarkIcon from './BookmarkIcon';

/**
 * Die gebuchte Card in der höchsten Stufe — „Bühne" (Design-Handoff Variante 1b, mobil 2a).
 *
 * Sie ist die einzige Stufe, die das Raster verlässt: über beide Spalten, mit dem Foto des Events
 * statt des generierten Kopfs und einem eigenen Knopf zur Anmeldung. Deshalb steht sie als eigene
 * Komponente neben Hero+Body und nicht als weitere Klasse an ihnen — ihre Reihenfolge ist eine
 * andere (Meta-Zeile über dem Titel, Datum als eigene Zeile, Fußzeile mit Knopf).
 *
 * Breit oder hoch entscheidet die **Breite der Card**, nicht das Datenmodell: unter 700px klappt
 * das Layout auf „Foto oben, Inhalt darunter" um (siehe App.css, Container-Query). Datum und Herz
 * stehen deshalb zweimal im Baum — je Layout an ihrem Platz, das jeweils andere per `display:none`
 * weg, also auch für Screenreader nur einmal vorhanden.
 *
 * Zwei Klickziele: die Card führt wie immer zur Detailseite (gespannter Titel-Link), der Knopf
 * dagegen direkt zur Anmeldung des Veranstalters — das ist die eigentlich verkaufte Leistung.
 */

const MON = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const WD = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const WD_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

interface Props {
  event: CyclingEvent;
  favorited: boolean;
  favoriteTitle?: string;
  onFavorite?: (e: React.MouseEvent) => void;
  userLat?: number | null;
  userLng?: number | null;
  to?: string;
  onNavigate?: (e: React.MouseEvent) => void;
  /**
   * Vorschau: die Card wird gezeigt, nicht bedient (Buchungs- und Admin-Dialog). Herz, Titel und
   * Knopf bleiben sichtbar, sind aber keine Klickziele — sonst lägen in einem Dialog fokussierbare
   * Elemente, die woanders hinführen als der Dialog selbst.
   */
  preview?: boolean;
}

const fmtDay = (d: Date): string => String(d.getDate()).padStart(2, '0');
const fmtMon = (d: Date): string => `${MON[d.getMonth()].toUpperCase()} ${String(d.getFullYear()).slice(2)}`;

const EventCardStage: React.FC<Props> = ({
  event, favorited, favoriteTitle, onFavorite, userLat, userLng, to, onNavigate, preview,
}) => {
  const promo = activePromotion(event);
  const start = new Date(`${event.eventDate}T00:00:00`);
  const end = event.endDate && event.endDate > event.eventDate
    ? new Date(`${event.endDate}T00:00:00`)
    : null;
  const weekday = !end
    ? WD[start.getDay()]
    : (end.getTime() - start.getTime()) / 86400000 < 7
      ? `${WD_SHORT[start.getDay()]}–${WD_SHORT[end.getDay()]}`
      : null;

  const color = CATEGORY_COLORS[event.category] ?? '#922C57';
  const categoryLabel = eventCategories(event).map(c => CATEGORY_LABELS[c] ?? c).join(' · ');
  const label = event.eventType
    ? `${categoryLabel} · ${EVENT_TYPE_LABELS[event.eventType] ?? event.eventType}`
    : categoryLabel;

  const registration = registrationBadge(event);
  const registrationChip = registration && (registration.tone === 'positive' || registration.tone === 'neutral')
    ? registration
    : null;

  const region = [event.city, displayBundesland(event.bundesland)].filter(Boolean).join(' · ');
  const distanceKm =
    userLat != null && userLng != null && event.latitude != null && event.longitude != null
      ? Math.round(haversineDistance(userLat, userLng, event.latitude, event.longitude) / 5) * 5
      : null;
  const countdown = eventCountdown(event);

  const { chips, more } = cardDistanceChips(event.distances);
  const cta = promotionCta(event);

  const dateBlock = event.permanent ? (
    /* Permanent: kein Termin, also kein Datum — das Badge mit der Saison steht an seiner Stelle. */
    <span className="eh-permanent">{permanentBadge(event)}</span>
  ) : (
    <>
      <span className="ecs-day">{end ? `${fmtDay(start)}–${fmtDay(end)}` : fmtDay(start)}</span>
      <span className="ecs-mon">{fmtMon(end ?? start)}</span>
      {weekday && <span className="ecs-wd">· {weekday}</span>}
    </>
  );

  const heart = (extraClass: string) => (preview ? (
    <span className={`eh-heart ecs-heart ${extraClass}`} aria-hidden>
      <BookmarkIcon filled={favorited} size={15} />
    </span>
  ) : (
    <button
      className={`eh-heart ecs-heart ${extraClass}`}
      onClick={onFavorite}
      title={favoriteTitle}
      aria-label={favoriteTitle}
    >
      <BookmarkIcon filled={favorited} size={15} />
    </button>
  ));

  return (
    <>
      <div className="ecs-photo">
        {/* Der Eventname genügt als Alternativtext — das Bild trägt keine eigene Aussage, es ist
            der Aufmacher. Ein zweiter Satz hier wäre für Screenreader nur Lärm. */}
        <img src={event.imageUrl} alt={event.title} loading="lazy" />
        <span className="ecs-scrim" aria-hidden />
        {promo && (
          <span className="ecs-label">
            <span className="sr-only">Bezahlte Hervorhebung: </span>
            {promo.label}
          </span>
        )}
        {/* Hoch-Layout: Herz oben rechts auf dem Foto, Datum unten links darauf. */}
        {heart('ecs-heart--photo')}
        <span className="ecs-date ecs-date--photo">{dateBlock}</span>
      </div>

      <div className="ecs-main">
        <div className="ecs-meta">
          <span className="ecb-cat" style={{ color }}>
            <span className="ecb-diamond" style={{ background: color }} />
            {label}
          </span>
          {registrationChip && (
            <span
              className={`ecb-reg ecb-reg--${registrationChip.tone === 'positive' ? 'open' : 'muted'}`}
              title={registrationChip.long}
            >
              <span className="ecb-reg-dot" />
              {registrationChip.label}
            </span>
          )}
          {/* Breit-Layout: das Herz hat hier Platz — anders als bei den kleinen Stufen, wo es sich
              den Kopf mit der Kennzeichnung teilt. */}
          {heart('ecs-heart--meta')}
        </div>

        <div className="ecs-date ecs-date--row">{dateBlock}</div>

        <h3 className="event-card-title ecs-title">
          {to && !preview
            ? <Link className="event-card-link" to={to} onClick={onNavigate}>{event.title}</Link>
            : event.title}
        </h3>

        {(region || countdown || distanceKm != null) && (
          <div className="ecb-region">
            {region}
            {distanceKm != null && <span className="ecb-dist">{region ? ' · ' : ''}ca. {distanceKm} km entfernt</span>}
            {countdown && (
              <span className="ecb-countdown">
                {region || distanceKm != null ? <span className="ecb-sep"> | </span> : ''}
                {countdown}
              </span>
            )}
          </div>
        )}

        <div className="ecs-foot">
          {chips.length > 0 && (
            <div className="ecb-chips">
              {chips.map(c => <span key={c} className="ecb-chip">{c}</span>)}
              {more && <span className="ecb-chip ecb-chip-more">{more}</span>}
            </div>
          )}
          {cta && (preview ? (
            <span className="ecs-cta">{cta.label} ›</span>
          ) : (
            /* Bezahlter Verweis auf eine fremde Seite: `sponsored` sagt Suchmaschinen genau das.
               `stopPropagation`, damit der Knopf nicht zusätzlich die Card-Navigation auslöst. */
            <a
              className="ecs-cta"
              href={cta.url}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              onClick={e => e.stopPropagation()}
            >
              {cta.label} ›
            </a>
          ))}
        </div>
      </div>
    </>
  );
};

export default EventCardStage;
